import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bone, MessageCircle, PawPrint } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import PawTrail from "./PawTrail";
import api from "../lib/api";

const DEFAULT_STORY = `Ari's Happy Tails is me — Ari. Not an app, not a rotation of strangers: when you book, I'm the one at your door.

I keep every walk, meal and nap exactly the way your pet knows them, and I send a photo after every visit so you never wonder how it's going.`;

const DEFAULT_HIGHLIGHTS = `Overnight house sitting
Boarding in my own home
Dog walking — 30 or 60 minutes
Drop-in visits & medication`;

const MeetAri = () => {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api
      .get("/profile")
      .then((r) => setProfile(r.data))
      .catch(() => setProfile({}));
  }, []);

  const story = (profile?.story || "").trim() || DEFAULT_STORY;
  const highlights = ((profile?.highlights || "").trim() || DEFAULT_HIGHLIGHTS)
    .split("\n")
    .map((h) => h.trim())
    .filter(Boolean);

  const archImage = profile?.story_image || profile?.image;

  return (
    <section id="meet-ari" className="border-t border-line bg-white/60 py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto max-w-[420px]">
              <div className="absolute -right-5 top-8 h-full w-full rounded-[999px_999px_28px_28px] bg-sand" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[999px_999px_28px_28px]">
                {archImage ? (
                  <img
                    src={archImage}
                    alt={`${profile?.name || "Ari"}, your pet sitter`}
                    className="h-[420px] w-full object-cover md:h-[540px]"
                    data-testid="meet-ari-photo"
                  />
                ) : (
                  <div className="flex h-[420px] w-full items-center justify-center bg-sand md:h-[540px]">
                    <PawPrint className="h-16 w-16 text-marmalade" fill="currentColor" strokeWidth={0} />
                  </div>
                )}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_15%,rgba(217,108,74,0.14),transparent_55%)]" />
              </div>
              <PawTrail count={4} className="absolute -bottom-6 -left-6 w-40" opacityClass="opacity-25" />
            </div>
          </Reveal>

          <div className="lg:col-span-7 lg:pl-6">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Meet Ari</p>
              <h2 className="mt-4 max-w-xl font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink md:text-6xl">
                {profile?.story_heading?.trim() || "The human behind the happy tails"}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-6 max-w-xl space-y-4">
                {story.split("\n").filter((p) => p.trim()).map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-inkSoft md:text-lg" data-testid={i === 0 ? "meet-ari-story" : undefined}>
                    {p.trim()}
                  </p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {highlights.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-2 rounded-full border border-line bg-cream px-4 py-2.5 text-sm text-ink"
                    data-testid="meet-ari-highlight"
                  >
                    <Bone className="h-3.5 w-3.5 text-marmalade" fill="currentColor" strokeWidth={0} />
                    {h}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/messages"
                data-testid="meet-ari-cta"
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-cream transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ink"
              >
                <MessageCircle className="h-4 w-4" />
                Say hello to Ari
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetAri;
